import React, { useState, useEffect } from 'react';
import { Appointment, AppointmentStatus } from '../../types/appointment.types';
import { Barber } from '../../types/barber.types';

interface WeeklyCalendarProps {
    appointments: Appointment[];
    barber?: Barber | null;
    onAppointmentClick?: (appointment: Appointment) => void;
    onSlotClick?: (date: Date, time: string) => void;
    onWeekChange?: (weekStart: Date) => void;
    initialWeekStart?: Date | null;
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
    appointments,
    barber,
    onAppointmentClick,
    onSlotClick,
    onWeekChange,
    initialWeekStart
}) => {
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(initialWeekStart ? getWeekStart(initialWeekStart) : getWeekStart(new Date()));
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const currentTimeRef = React.useRef<HTMLDivElement>(null);
    const hourRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const scrollDoneRef = React.useRef<string | null>(null);

    // Scroll logic on initial load or week change
    useEffect(() => {
        const currentWeekKey = currentWeekStart.toISOString();
        if (scrollDoneRef.current === currentWeekKey && appointments.length > 0 && barber) return;

        const timer = setTimeout(() => {
            const now = new Date();
            const todayStr = formatDate(now);
            const isCurrentWeek = getWeekDays(currentWeekStart).some(d => formatDate(d) === todayStr);
            const allHours = getHours();

            let shiftStartStr = '09:15';
            let shiftEndStr = '22:00';

            if (barber && barber.schedules && barber.schedules.length > 0) {
                const dayEnum = getDayOfWeekEnum(now);
                let schedule = barber.schedules.find(s => s.dayOfWeek === dayEnum && s.isAvailable);
                if (!schedule) {
                    schedule = barber.schedules.find(s => s.isAvailable);
                }
                if (schedule) {
                    shiftStartStr = schedule.startTime.substring(0, 5);
                    shiftEndStr = schedule.endTime.substring(0, 5);
                }
            } else if (allHours.length > 0) {
                shiftStartStr = allHours[0];
                shiftEndStr = allHours[allHours.length - 1];
            }

            const scrollToTarget = (targetTimeStr: string, isCurrentWeek: boolean) => {
                let closestHourStr = allHours[0];
                let minDiff = Infinity;
                
                const [th, tm] = targetTimeStr.split(':');
                const targetMins = parseInt(th, 10) * 60 + parseInt(tm, 10);

                for (const hStr of allHours) {
                    const [h, m] = hStr.split(':');
                    const hMins = parseInt(h, 10) * 60 + parseInt(m, 10);
                    const diff = Math.abs(hMins - targetMins);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestHourStr = hStr;
                    }
                }

                if (isCurrentWeek) {
                    const el = document.getElementById(`cell-today-${closestHourStr}`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
                    }
                } else {
                    const el = hourRefs.current[closestHourStr];
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            };

            if (isCurrentWeek) {
                const currentMinutes = now.getHours() * 60 + now.getMinutes();
                const [sh, sm] = shiftStartStr.split(':');
                const shiftStartMinutes = parseInt(sh, 10) * 60 + parseInt(sm, 10);
                
                const [eh, em] = shiftEndStr.split(':');
                const shiftEndMinutes = parseInt(eh, 10) * 60 + parseInt(em, 10) + 45;

                if (currentMinutes < shiftStartMinutes || currentMinutes >= shiftEndMinutes) {
                    scrollToTarget(shiftStartStr, true);
                } else {
                    if (currentTimeRef.current) {
                        currentTimeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
                    } else {
                        const currentHourStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                        scrollToTarget(currentHourStr, true);
                    }
                }
            } else {
                scrollToTarget(shiftStartStr, false);
            }
            scrollDoneRef.current = currentWeekKey;
        }, 150);
        return () => clearTimeout(timer);
    }, [currentWeekStart, appointments, barber]);

    // Update current time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Update every minute
        return () => clearInterval(interval);
    }, []);

    // Notify parent on initial load
    useEffect(() => {
        onWeekChange?.(currentWeekStart);
    }, []); // Only on mount

    function getWeekStart(date: Date): Date {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
        return new Date(d.setDate(diff));
    }

    function getWeekDays(weekStart: Date): Date[] {
        const days: Date[] = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + i);
            days.push(day);
        }
        return days;
    }

    function formatDate(date: Date): string {
        // Use local timezone, not UTC (toISOString uses UTC which shifts dates)
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function formatDayHeader(date: Date): string {
        const days = ['DO', 'LU', 'MA', 'MI', 'JU', 'VI', 'SA'];
        return days[date.getDay()];
    }

    function isToday(date: Date): boolean {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    }

    function goToToday() {
        const newWeekStart = getWeekStart(new Date());
        setCurrentWeekStart(newWeekStart);
        onWeekChange?.(newWeekStart);
    }

    function goToPreviousWeek() {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(currentWeekStart.getDate() - 7);
        setCurrentWeekStart(newDate);
        onWeekChange?.(newDate);
    }

    function goToNextWeek() {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(currentWeekStart.getDate() + 7);
        setCurrentWeekStart(newDate);
        onWeekChange?.(newDate);
    }

    function getMonthYearLabel(weekStart: Date): string {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const startMonth = weekStart.toLocaleString('es-ES', { month: 'long' });
        const endMonth = weekEnd.toLocaleString('es-ES', { month: 'long' });
        const year = weekStart.getFullYear();

        if (startMonth === endMonth) {
            return `${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} ${year}`;
        } else {
            return `${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} - ${endMonth.charAt(0).toUpperCase() + endMonth.slice(1)} ${year}`;
        }
    }

    function getDayOfWeekEnum(date: Date): string {
        const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
        return days[date.getDay()];
    }

    const fixedBlocks = [
        '09:15', '10:00', '10:45', '11:30', '12:15', '13:00',
        '13:45', '14:30', '15:15', '16:00', '16:45', '17:30',
        '18:15', '19:00', '19:45', '20:30', '21:15', '22:00'
    ];

    function getHours(): string[] {
        const aptTimes = appointments.map(apt => apt.startTime.substring(0, 5));
        const allTimes = Array.from(new Set([...fixedBlocks, ...aptTimes]));
        return allTimes.sort();
    }

    function getAppointmentsForDayAndHour(date: Date, hour: string): Appointment[] {
        const dateStr = formatDate(date);
        return appointments.filter(apt => {
            if (apt.appointmentDate !== dateStr) return false;
            return apt.startTime.substring(0, 5) === hour;
        });
    }




    function getStatusColor(status: AppointmentStatus): string {
        const colors: Record<AppointmentStatus, string> = {
            PENDING: '#f59e0b',
            CONFIRMED: '#3b82f6',
            IN_PROGRESS: '#8b5cf6',
            COMPLETED: '#10b981',
            CANCELLED: '#ef4444',
            NO_SHOW: '#6b7280'
        };
        return colors[status] || '#6b7280';
    }

    const weekDays = getWeekDays(currentWeekStart);
    const hours = getHours();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#111111',
                borderRadius: '0.75rem',
                border: '1px solid #2a2a2a'
            }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#f59e0b' }}>
                        {getMonthYearLabel(currentWeekStart)}
                    </h2>
                    <button
                        className="btn btn-sm"
                        onClick={goToToday}
                        style={{ padding: '0.25rem 0.75rem', background: '#f59e0b20', border: '1px solid #f59e0b50', color: '#f59e0b', borderRadius: '0.5rem', fontWeight: '600' }}
                    >
                        HOY
                    </button>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={goToPreviousWeek}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#a3a3a3',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                            e.currentTarget.style.borderColor = '#555';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                            e.currentTarget.style.borderColor = '#333';
                        }}
                    >
                        <span style={{ fontSize: '1rem' }}>◀</span>
                        <span>Anterior</span>
                    </button>
                    <button
                        onClick={goToNextWeek}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#a3a3a3',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                            e.currentTarget.style.borderColor = '#555';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                            e.currentTarget.style.borderColor = '#333';
                        }}
                    >
                        <span>Siguiente</span>
                        <span style={{ fontSize: '1rem' }}>▶</span>
                    </button>
                </div>
            </div>

            {/* Calendar Grid Container */}
            <div 
                id="weekly-scroll-container"
                style={{
                backgroundColor: '#111111',
                borderRadius: '0.75rem',
                border: '1px solid #2a2a2a',
                overflow: 'auto',
                maxHeight: '70vh',
                position: 'relative'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '40px repeat(7, minmax(0, 1fr))',
                    width: '100%',
                    position: 'relative'
                }}>
                    {/* Header Row - Days */}
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        backgroundColor: '#1a1a1a',
                        borderBottom: '2px solid #2a2a2a',
                        zIndex: 40
                    }}></div>
                    {weekDays.map((day, idx) => (
                        <div
                            key={idx}
                            style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: isToday(day) ? '#f59e0b15' : '#1a1a1a',
                                borderBottom: '2px solid #2a2a2a',
                                borderLeft: idx > 0 ? '1px solid #2a2a2a' : 'none',
                                padding: '0.5rem 0.2rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                zIndex: 30
                            }}
                        >
                            <div style={{ fontSize: '0.65rem', color: '#737373', marginBottom: '0.1rem' }}>
                                {formatDayHeader(day)}
                            </div>
                            <div style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: isToday(day) ? '#f59e0b' : '#e5e5e5'
                            }}>
                                {day.getDate()}
                            </div>
                        </div>
                    ))}

                    {/* Time Rows */}
                    {hours.map((hour, hourIdx) => (
                        <React.Fragment key={hour}>
                            {/* Hour Label */}
                            <div 
                                ref={el => { hourRefs.current[hour] = el; }}
                                style={{
                                position: 'sticky',
                                left: 0,
                                borderTop: hourIdx > 0 ? '1px solid #2a2a2a' : 'none',
                                padding: '0.5rem 0.1rem',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                color: '#f59e0b',
                                textAlign: 'center',
                                backgroundColor: '#1a1a1a',
                                zIndex: 20,
                                scrollMarginTop: '70px'
                            }}>
                                {hour}
                            </div>

                            {/* Day Cells */}
                            {weekDays.map((day, dayIdx) => {
                                const dayAppointments = getAppointmentsForDayAndHour(day, hour);
                                const isCurrentDay = isToday(day);
                                return (
                                    <div
                                        id={isCurrentDay ? `cell-today-${hour}` : undefined}
                                        key={`${hourIdx}-${dayIdx}`}
                                        style={{
                                            borderTop: hourIdx > 0 ? '1px solid #2a2a2a' : 'none',
                                            borderLeft: '1px solid #2a2a2a',
                                            backgroundColor: isCurrentDay ? '#f59e0b08' : '#111111',
                                            padding: '0.1rem',
                                            minHeight: '50px',
                                            position: 'relative',
                                            zIndex: 10,
                                            scrollMarginTop: '70px'
                                        }}
                                    >
                                        {/* Single clickable 45-min block background */}
                                        <div 
                                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, cursor: 'pointer' }}
                                            onClick={() => onSlotClick?.(day, hour)}
                                        />

                                        {/* Appointments Container */}
                                        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            {dayAppointments.map((apt) => (
                                                <div
                                                    key={apt.id}
                                                    onClick={() => onAppointmentClick?.(apt)}
                                                style={{
                                                    background: `linear-gradient(135deg, ${getStatusColor(apt.status)}, ${getStatusColor(apt.status)}dd)`,
                                                    borderLeft: `2px solid ${getStatusColor(apt.status)}`,
                                                    padding: '0.2rem',
                                                    marginBottom: '0.1rem',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.6rem',
                                                    cursor: onAppointmentClick ? 'pointer' : 'default',
                                                    transition: 'all 0.2s',
                                                    boxShadow: `0 1px 3px ${getStatusColor(apt.status)}40`,
                                                    overflow: 'hidden'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'scale(1.03)';
                                                    e.currentTarget.style.boxShadow = `0 4px 12px ${getStatusColor(apt.status)}60`;
                                                    e.currentTarget.style.zIndex = '50';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                    e.currentTarget.style.boxShadow = `0 1px 3px ${getStatusColor(apt.status)}40`;
                                                    e.currentTarget.style.zIndex = '2';
                                                }}
                                            >
                                                <div style={{
                                                    fontWeight: '600',
                                                    color: 'rgba(255,255,255,0.85)',
                                                    marginBottom: '0.1rem',
                                                    fontSize: '0.55rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {apt.startTime.substring(0, 5)} - {apt.endTime.substring(0, 5)}
                                                </div>
                                                <div style={{
                                                    fontWeight: '700',
                                                    color: '#fff',
                                                    marginBottom: '0.1rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {apt.clientName}
                                                </div>
                                                <div style={{ 
                                                    color: 'rgba(255,255,255,0.75)', 
                                                    fontSize: '0.55rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {apt.serviceDescription || 'Servicio'}
                                                </div>
                                            </div>
                                        ))}
                                        </div>

                                        {/* Current Time Indicator - Calculate if current time is within this 45-min block */}
                                        {(() => {
                                            if (!isCurrentDay) return null;
                                            const [blockHourStr, blockMinuteStr] = hour.split(':');
                                            const blockTimeMinutes = parseInt(blockHourStr, 10) * 60 + parseInt(blockMinuteStr, 10);
                                            const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
                                            const isCurrentBlock = currentMinutes >= blockTimeMinutes && currentMinutes < blockTimeMinutes + 45;
                                            
                                            if (isCurrentBlock) {
                                                const percentage = ((currentMinutes - blockTimeMinutes) / 45) * 100;
                                                return (
                                                    <div 
                                                        ref={currentTimeRef}
                                                        style={{
                                                        position: 'absolute',
                                                        top: `${percentage}%`,
                                                        left: '-1px',
                                                        right: 0,
                                                        height: '2px',
                                                        backgroundColor: '#ef4444',
                                                        zIndex: 100,
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '-4px',
                                                            top: '-3px',
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#ef4444'
                                                        }} />
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WeeklyCalendar;
